import { Geometry2, Geometry3, IDataLayerType, IGeometry, LayerTypeId, LayerViewModel, map, selectMany } from "../omnia"

/**
 * Returns all layers + their "accessible" variants
 * At the moment, only pH variants are made accessible here
 * @param layers Source layers
 */
export function getAllLayers(layers: LayerViewModel[], includeVariants = true) {

    return selectMany((layers || [])
        .filter(l => !l.isVariant())
        .map(l => {

            const variants = includeVariants && shouldIncludeType(l.typeId)
                ? l.getVariants() || []
                : []

            return [l].concat(variants)

        }))

    function shouldIncludeType(id: LayerTypeId) {

        switch (id) {
            case LayerTypeId.pH:
            case LayerTypeId.Clay:
                return true

            default:
                return false
        }
    }

}

export function getLayerTypeOrder(lt: IDataLayerType): number {
    if (lt.GenericFeatures) return 1;
    if (lt.TypeId == LayerTypeId.Picture) return 2;
    return 0;
}

/**
 * Returns a function which can be used to clip feature to an outer boudary
 * @param topo Topology service
 * @param outerBoundary Outer boundary
 */
export function getClippingFunction(topo: omnia.map.IMapTopologyService, outerBoundary: Geometry2) {

    const outer = topo.getGeometry(outerBoundary, true)

    return (g: IGeometry) => {

        let r = clipGeometry(topo, outer, g)

        if (r.remove) {
            return null
        }

        if (!r.result) {
            return r.geom
        }

        const wkt = r.result.getWKT()
        if (!wkt) return null

        return {
            Geometry: {
                CoordinateSystemId: 3857,
                WellKnownText: wkt
            }
        }
    }

}

export function clipGeometry<T extends Geometry3>(topo: omnia.map.IMapTopologyService, outer: map.IMapTopologyGeometry, geom: T, minArea = 0) {

    const inner = topo.getGeometry(geom, true)
    if (!inner) return { geom, result: null, cause: "inner_null" }

    if (topo.contains(outer, inner)) {
        return { geom, result: null, cause: "contained" }
    }

    if (topo.disjoint(outer, inner)) {
        return { geom, result: null, remove: true, cause: "disjoint" }
    }

    let result = topo.intersection(inner, outer)
    if (!result) {
        return { geom: null, result: null, cause: "no_intersection" }
    }

    if (!result.isValid()) {

        result = topo.makeValid(result)

        if (!result.isValid()) {
            return { geom: null, result: null, cause: "invalid_result" }
        }

    }

    if (result.getArea() < minArea) {
        return { geom, result: null, remove: true, cause: "area_too_small" }
    }

    // console.log("clipGeometry", { outer: outer.getWKT(), inner: inner.getWKT(), result: result.getWKT(), valid: result.isValid(), dim: result.getDimension(), area: result.getArea(), type: result.getType() })

    return { geom, result }

}
